import { create } from 'zustand';
import { immer } from 'zustand/middleware/immer';
import { persist, createJSONStorage } from 'zustand/middleware';
import type { ConditionName } from '@character-creator/types';
import type { CritStyle } from '@character-creator/core';
import { applyDamage, applyHealing, applyTempHp, calculateLongRestEffects } from '@character-creator/core';
import type { RuntimeState } from '../utils/characterExport';

// Canonical default order — used for initialization and migration
const DEFAULT_RP_SECTION_ORDER = [
  'identity', 'personality', 'motivations', 'backstory',
  'features', 'tools', 'feats', 'equipment', 'notes',
] as const;

export type BuiltInSectionId = typeof DEFAULT_RP_SECTION_ORDER[number];

export { DEFAULT_RP_SECTION_ORDER };

export interface CustomEffect {
  id: string;
  name: string;
  description: string;
}

export interface RpCustomSection {
  id: string;        // `custom-${crypto.randomUUID()}`
  title: string;     // user-defined name
  content: string;   // freeform text
}

export interface CustomCheatSheetTip {
  id: string;           // 'custom-<uuid>'
  icon: string;
  title: string;
  description: string;
}

export interface CheatSheetState {
  dismissedTipIds: string[];         // auto-generated tips user has hidden (recoverable)
  customTips: CustomCheatSheetTip[]; // user-added tips
  tipOrder: string[];                // ordered tip IDs (auto + custom, for reordering)
  combatNotes: string;               // freeform text section
}

export const createDefaultCheatSheet = (): CheatSheetState => ({
  dismissedTipIds: [],
  customTips: [],
  tipOrder: [],
  combatNotes: '',
});

export interface RpLayoutConfig {
  sectionOrder: string[];           // ordered section IDs (built-in + custom)
  hiddenSections: string[];         // section IDs to hide
  customSections: RpCustomSection[]; // user-created sections
}

export const createDefaultRpLayout = (): RpLayoutConfig => ({
  sectionOrder: [...DEFAULT_RP_SECTION_ORDER],
  hiddenSections: [],
  customSections: [],
});

export interface DiceRoll {
  id: string;
  type: string; // 'Persuasion', 'STR Save', 'Longsword Attack', etc.
  d20Roll: number;
  modifier: number;
  total: number;
  advantage?: 'advantage' | 'disadvantage';
  timestamp: number;
}

export interface SpellSlotState {
  max: number;
  used: number;
}

export interface PlaySession {
  // Character identification
  characterId: string; // Character UUID for unique session identification

  // HP tracking
  currentHp: number;
  maxHp: number;
  tempHp: number;

  // Death saves
  deathSaveSuccesses: number;
  deathSaveFailures: number;

  // Spell slots (indexed by level 1-9, plus 'pact' for warlock pact slots)
  spellSlots: Record<string, SpellSlotState>;

  // Hit dice (indexed by die type, e.g., 'd8', 'd10')
  hitDice: Record<string, { max: number; used: number }>;

  // Conditions
  activeConditions: ConditionName[];
  exhaustionLevel: number; // 0-6

  // Concentration
  concentrationSpell: string | null;

  // Class resources (key = resource name like 'Sorcery Points', value = { max, used })
  classResources: Record<string, { max: number; used: number }>;

  // Combat toggles
  combatToggles: {
    sneakAttackEnabled: boolean;
    critStyle: CritStyle;
    critActive: boolean;
  };

  // Combat layout preference
  layoutPreference: 'auto' | 'combat-priority' | 'tab-within-tab';

  // Roll history
  rollHistory: DiceRoll[];

  // Session notes
  sessionNotes: string;

  // Inspirations
  bardicInspiration: boolean;
  dmInspiration: boolean;

  // Custom temporary effects
  customEffects: CustomEffect[];

  // RP tab layout customization
  rpLayout: RpLayoutConfig;

  // Cheat sheet (tips + combat notes)
  cheatSheet: CheatSheetState;

  // Last updated
  lastUpdated: number;
}

interface PlaySessionState {
  // Current active session
  activeSession: PlaySession | null;

  // Actions
  initializeSession: (
    characterId: string,
    maxHp: number,
    spellSlots: Record<string, SpellSlotState>,
    hitDice: Record<string, { max: number; used: number }>,
    classResources: Record<string, { max: number; used: number }>
  ) => void;

  loadSession: (characterId: string) => boolean;

  // HP actions
  takeDamage: (amount: number) => void;
  heal: (amount: number) => void;
  setTempHp: (amount: number) => void;
  setMaxHp: (amount: number) => void;

  // Death saves
  addDeathSaveSuccess: () => void;
  addDeathSaveFailure: () => void;
  resetDeathSaves: () => void;

  // Spell slots
  useSpellSlot: (level: string) => void;
  restoreSpellSlot: (level: string) => void;
  restoreAllSpellSlots: () => void;

  // Hit dice
  useHitDie: (dieType: string) => number | null; // Returns roll result
  restoreHitDice: (amount: number) => void; // Restore on long rest (half of max)

  // Conditions
  toggleCondition: (condition: ConditionName) => void;
  setExhaustionLevel: (level: number) => void;
  clearAllConditions: () => void;

  // Concentration
  setConcentration: (spellName: string | null) => void;

  // Class resources
  useResource: (resourceName: string, amount?: number) => void;
  restoreResource: (resourceName: string, amount?: number) => void;
  restoreAllResources: () => void;

  // Rolling
  addRoll: (roll: Omit<DiceRoll, 'id' | 'timestamp'>) => void;
  clearRollHistory: () => void;

  // Notes
  setSessionNotes: (notes: string) => void;

  // Combat toggles
  setSneakAttackEnabled: (enabled: boolean) => void;
  setCritStyle: (style: CritStyle) => void;
  setCritActive: (active: boolean) => void;

  // Combat layout
  setLayoutPreference: (preference: 'auto' | 'combat-priority' | 'tab-within-tab') => void;

  // Inspirations
  toggleBardicInspiration: () => void;
  toggleDmInspiration: () => void;

  // Custom effects
  addCustomEffect: (name: string, description: string) => void;
  removeCustomEffect: (id: string) => void;

  // RP layout customization
  setRpSectionOrder: (order: string[]) => void;
  toggleRpSectionVisibility: (sectionId: string) => void;
  addRpCustomSection: (title: string) => void;
  updateRpCustomSection: (id: string, updates: Partial<{ title: string; content: string }>) => void;
  removeRpCustomSection: (id: string) => void;
  moveRpSection: (sectionId: string, direction: 'up' | 'down') => void;

  // Cheat sheet actions
  dismissCheatSheetTip: (tipId: string) => void;
  restoreCheatSheetTip: (tipId: string) => void;
  addCustomCheatSheetTip: (title: string, description: string) => void;
  removeCustomCheatSheetTip: (tipId: string) => void;
  reorderCheatSheetTip: (tipId: string, direction: 'up' | 'down') => void;
  setCombatNotes: (notes: string) => void;
  updateCheatSheetTipOrder: (mergedOrder: string[]) => void;

  // Rest actions
  shortRest: () => void;
  longRest: () => void;

  // Clear session
  clearSession: () => void;

  // Export/Import helpers
  getExportableRuntimeState: () => RuntimeState | null;
  restoreRuntimeState: (runtimeState: RuntimeState) => void;
}

const createEmptySession = (characterId: string): PlaySession => ({
  characterId,
  currentHp: 0,
  maxHp: 0,
  tempHp: 0,
  deathSaveSuccesses: 0,
  deathSaveFailures: 0,
  spellSlots: {},
  hitDice: {},
  activeConditions: [],
  exhaustionLevel: 0,
  concentrationSpell: null,
  classResources: {},
  combatToggles: {
    sneakAttackEnabled: false,
    critStyle: 'standard' as const,
    critActive: false,
  },
  layoutPreference: 'auto',
  rollHistory: [],
  sessionNotes: '',
  bardicInspiration: false,
  dmInspiration: false,
  customEffects: [],
  rpLayout: createDefaultRpLayout(),
  cheatSheet: createDefaultCheatSheet(),
  lastUpdated: Date.now(),
});

export const usePlaySessionStore = create<PlaySessionState>()(
  persist(
    immer((set, get) => ({
      activeSession: null,

      initializeSession: (characterId, maxHp, spellSlots, hitDice, classResources) => {
        set((state) => {
          state.activeSession = {
            ...createEmptySession(characterId),
            currentHp: maxHp,
            maxHp,
            spellSlots,
            hitDice,
            classResources,
            lastUpdated: Date.now(),
          };
        });
      },

      loadSession: (characterId) => {
        const state = get();
        if (state.activeSession?.characterId === characterId) {
          return true;
        }
        // Session loading happens automatically via persist middleware
        // If no matching session exists, return false
        return state.activeSession?.characterId === characterId;
      },

      takeDamage: (amount) => {
        set((state) => {
          if (!state.activeSession) return;
          const result = applyDamage(state.activeSession.currentHp, state.activeSession.tempHp, amount);
          state.activeSession.currentHp = result.currentHp;
          state.activeSession.tempHp = result.tempHp;
          state.activeSession.lastUpdated = Date.now();
        });
      },

      heal: (amount) => {
        set((state) => {
          if (!state.activeSession) return;
          const result = applyHealing(state.activeSession.currentHp, state.activeSession.maxHp, amount);
          state.activeSession.currentHp = result.currentHp;
          if (result.shouldResetDeathSaves) {
            state.activeSession.deathSaveSuccesses = 0;
            state.activeSession.deathSaveFailures = 0;
          }
          state.activeSession.lastUpdated = Date.now();
        });
      },

      setTempHp: (amount) => {
        set((state) => {
          if (!state.activeSession) return;
          state.activeSession.tempHp = applyTempHp(state.activeSession.tempHp, amount);
          state.activeSession.lastUpdated = Date.now();
        });
      },

      setMaxHp: (amount) => {
        set((state) => {
          if (!state.activeSession) return;
          state.activeSession.maxHp = amount;
          state.activeSession.currentHp = Math.min(state.activeSession.currentHp, amount);
          state.activeSession.lastUpdated = Date.now();
        });
      },

      addDeathSaveSuccess: () => {
        set((state) => {
          if (!state.activeSession) return;
          state.activeSession.deathSaveSuccesses = Math.min(3, state.activeSession.deathSaveSuccesses + 1);
          state.activeSession.lastUpdated = Date.now();
        });
      },

      addDeathSaveFailure: () => {
        set((state) => {
          if (!state.activeSession) return;
          state.activeSession.deathSaveFailures = Math.min(3, state.activeSession.deathSaveFailures + 1);
          state.activeSession.lastUpdated = Date.now();
        });
      },

      resetDeathSaves: () => {
        set((state) => {
          if (!state.activeSession) return;
          state.activeSession.deathSaveSuccesses = 0;
          state.activeSession.deathSaveFailures = 0;
          state.activeSession.lastUpdated = Date.now();
        });
      },

      useSpellSlot: (level) => {
        set((state) => {
          if (!state.activeSession) return;
          const slot = state.activeSession.spellSlots[level];
          if (slot && slot.used < slot.max) {
            slot.used += 1;
            state.activeSession.lastUpdated = Date.now();
          }
        });
      },

      restoreSpellSlot: (level) => {
        set((state) => {
          if (!state.activeSession) return;
          const slot = state.activeSession.spellSlots[level];
          if (slot && slot.used > 0) {
            slot.used -= 1;
            state.activeSession.lastUpdated = Date.now();
          }
        });
      },

      restoreAllSpellSlots: () => {
        set((state) => {
          if (!state.activeSession) return;
          Object.values(state.activeSession.spellSlots).forEach((slot) => {
            slot.used = 0;
          });
          state.activeSession.lastUpdated = Date.now();
        });
      },

      useHitDie: (dieType) => {
        const state = get();
        if (!state.activeSession) return null;

        const hitDie = state.activeSession.hitDice[dieType];
        if (!hitDie || hitDie.used >= hitDie.max) return null;

        // Roll the hit die
        const dieSize = parseInt(dieType.substring(1)); // 'd8' -> 8
        const roll = Math.floor(Math.random() * dieSize) + 1;

        set((s) => {
          if (!s.activeSession) return;
          s.activeSession.hitDice[dieType].used += 1;
          s.activeSession.lastUpdated = Date.now();
        });

        return roll;
      },

      restoreHitDice: (amount) => {
        set((state) => {
          if (!state.activeSession) return;
          // Restore hit dice distributed across die types (typically half of max on long rest)
          let remaining = amount;
          // Sort by die size (larger dice first - d12 before d6)
          const dieTypes = Object.keys(state.activeSession.hitDice).sort((a, b) => {
            const aSize = parseInt(a.substring(1), 10);
            const bSize = parseInt(b.substring(1), 10);
            return bSize - aSize;
          });
          for (const dieType of dieTypes) {
            if (remaining <= 0) break;
            const hitDie = state.activeSession.hitDice[dieType];
            const toRestore = Math.min(remaining, hitDie.used);
            hitDie.used -= toRestore;
            remaining -= toRestore;
          }
          state.activeSession.lastUpdated = Date.now();
        });
      },

      toggleCondition: (condition) => {
        set((state) => {
          if (!state.activeSession) return;
          const idx = state.activeSession.activeConditions.indexOf(condition);
          if (idx >= 0) {
            state.activeSession.activeConditions.splice(idx, 1);
          } else {
            state.activeSession.activeConditions.push(condition);
          }
          state.activeSession.lastUpdated = Date.now();
        });
      },

      setExhaustionLevel: (level) => {
        set((state) => {
          if (!state.activeSession) return;
          state.activeSession.exhaustionLevel = Math.max(0, Math.min(6, level));
          state.activeSession.lastUpdated = Date.now();
        });
      },

      clearAllConditions: () => {
        set((state) => {
          if (!state.activeSession) return;
          state.activeSession.activeConditions = [];
          state.activeSession.exhaustionLevel = 0;
          state.activeSession.lastUpdated = Date.now();
        });
      },

      setConcentration: (spellName) => {
        set((state) => {
          if (!state.activeSession) return;
          state.activeSession.concentrationSpell = spellName;
          state.activeSession.lastUpdated = Date.now();
        });
      },

      useResource: (resourceName, amount = 1) => {
        set((state) => {
          if (!state.activeSession) return;
          const resource = state.activeSession.classResources[resourceName];
          if (resource) {
            resource.used = Math.min(resource.max, resource.used + amount);
            state.activeSession.lastUpdated = Date.now();
          }
        });
      },

      restoreResource: (resourceName, amount = 1) => {
        set((state) => {
          if (!state.activeSession) return;
          const resource = state.activeSession.classResources[resourceName];
          if (resource) {
            resource.used = Math.max(0, resource.used - amount);
            state.activeSession.lastUpdated = Date.now();
          }
        });
      },

      restoreAllResources: () => {
        set((state) => {
          if (!state.activeSession) return;
          Object.values(state.activeSession.classResources).forEach((resource) => {
            resource.used = 0;
          });
          state.activeSession.lastUpdated = Date.now();
        });
      },

      addRoll: (roll) => {
        set((state) => {
          if (!state.activeSession) return;
          const newRoll: DiceRoll = {
            ...roll,
            id: crypto.randomUUID(),
            timestamp: Date.now(),
          };
          state.activeSession.rollHistory.unshift(newRoll);
          // Keep only last 20 rolls
          if (state.activeSession.rollHistory.length > 20) {
            state.activeSession.rollHistory = state.activeSession.rollHistory.slice(0, 20);
          }
          state.activeSession.lastUpdated = Date.now();
        });
      },

      clearRollHistory: () => {
        set((state) => {
          if (!state.activeSession) return;
          state.activeSession.rollHistory = [];
          state.activeSession.lastUpdated = Date.now();
        });
      },

      setSessionNotes: (notes) => {
        set((state) => {
          if (!state.activeSession) return;
          state.activeSession.sessionNotes = notes;
          state.activeSession.lastUpdated = Date.now();
        });
      },

      setSneakAttackEnabled: (enabled) => {
        set((state) => {
          if (!state.activeSession) return;
          state.activeSession.combatToggles.sneakAttackEnabled = enabled;
          state.activeSession.lastUpdated = Date.now();
        });
      },

      setCritStyle: (style) => {
        set((state) => {
          if (!state.activeSession) return;
          state.activeSession.combatToggles.critStyle = style;
          state.activeSession.lastUpdated = Date.now();
        });
      },

      setCritActive: (active) => {
        set((state) => {
          if (!state.activeSession) return;
          state.activeSession.combatToggles.critActive = active;
          state.activeSession.lastUpdated = Date.now();
        });
      },

      setLayoutPreference: (preference) => {
        set((state) => {
          if (!state.activeSession) return;
          state.activeSession.layoutPreference = preference;
          state.activeSession.lastUpdated = Date.now();
        });
      },

      setRpSectionOrder: (order) => {
        set((state) => {
          if (!state.activeSession) return;
          const layout = state.activeSession.rpLayout ?? createDefaultRpLayout();
          layout.sectionOrder = order;
          state.activeSession.rpLayout = layout;
          state.activeSession.lastUpdated = Date.now();
        });
      },

      toggleRpSectionVisibility: (sectionId) => {
        set((state) => {
          if (!state.activeSession) return;
          const layout = state.activeSession.rpLayout ?? createDefaultRpLayout();
          const idx = layout.hiddenSections.indexOf(sectionId);
          if (idx >= 0) {
            layout.hiddenSections.splice(idx, 1);
          } else {
            layout.hiddenSections.push(sectionId);
          }
          state.activeSession.rpLayout = layout;
          state.activeSession.lastUpdated = Date.now();
        });
      },

      addRpCustomSection: (title) => {
        set((state) => {
          if (!state.activeSession) return;
          const layout = state.activeSession.rpLayout ?? createDefaultRpLayout();
          const id = `custom-${crypto.randomUUID()}`;
          layout.customSections.push({ id, title, content: '' });
          layout.sectionOrder.push(id);
          state.activeSession.rpLayout = layout;
          state.activeSession.lastUpdated = Date.now();
        });
      },

      updateRpCustomSection: (id, updates) => {
        set((state) => {
          if (!state.activeSession) return;
          const layout = state.activeSession.rpLayout ?? createDefaultRpLayout();
          const section = layout.customSections.find((s) => s.id === id);
          if (section) {
            if (updates.title !== undefined) section.title = updates.title;
            if (updates.content !== undefined) section.content = updates.content;
          }
          state.activeSession.rpLayout = layout;
          state.activeSession.lastUpdated = Date.now();
        });
      },

      removeRpCustomSection: (id) => {
        set((state) => {
          if (!state.activeSession) return;
          const layout = state.activeSession.rpLayout ?? createDefaultRpLayout();
          layout.customSections = layout.customSections.filter((s) => s.id !== id);
          layout.sectionOrder = layout.sectionOrder.filter((s) => s !== id);
          layout.hiddenSections = layout.hiddenSections.filter((s) => s !== id);
          state.activeSession.rpLayout = layout;
          state.activeSession.lastUpdated = Date.now();
        });
      },

      moveRpSection: (sectionId, direction) => {
        set((state) => {
          if (!state.activeSession) return;
          const layout = state.activeSession.rpLayout ?? createDefaultRpLayout();
          const order = layout.sectionOrder;
          const idx = order.indexOf(sectionId);
          if (idx < 0) return;
          const targetIdx = direction === 'up' ? idx - 1 : idx + 1;
          if (targetIdx < 0 || targetIdx >= order.length) return;
          [order[idx], order[targetIdx]] = [order[targetIdx], order[idx]];
          state.activeSession.rpLayout = layout;
          state.activeSession.lastUpdated = Date.now();
        });
      },

      dismissCheatSheetTip: (tipId) => {
        set((state) => {
          if (!state.activeSession) return;
          const cs = state.activeSession.cheatSheet ?? createDefaultCheatSheet();
          if (!cs.dismissedTipIds.includes(tipId)) {
            cs.dismissedTipIds.push(tipId);
          }
          state.activeSession.cheatSheet = cs;
          state.activeSession.lastUpdated = Date.now();
        });
      },

      restoreCheatSheetTip: (tipId) => {
        set((state) => {
          if (!state.activeSession) return;
          const cs = state.activeSession.cheatSheet ?? createDefaultCheatSheet();
          cs.dismissedTipIds = cs.dismissedTipIds.filter((id) => id !== tipId);
          state.activeSession.cheatSheet = cs;
          state.activeSession.lastUpdated = Date.now();
        });
      },

      addCustomCheatSheetTip: (title, description) => {
        set((state) => {
          if (!state.activeSession) return;
          const trimmedTitle = title.trim();
          if (!trimmedTitle) return;
          const cs = state.activeSession.cheatSheet ?? createDefaultCheatSheet();
          const id = `custom-${crypto.randomUUID()}`;
          cs.customTips.push({ id, icon: '\u{1F4DD}', title: trimmedTitle, description });
          cs.tipOrder.push(id);
          state.activeSession.cheatSheet = cs;
          state.activeSession.lastUpdated = Date.now();
        });
      },

      removeCustomCheatSheetTip: (tipId) => {
        set((state) => {
          if (!state.activeSession) return;
          const cs = state.activeSession.cheatSheet ?? createDefaultCheatSheet();
          cs.customTips = cs.customTips.filter((t) => t.id !== tipId);
          cs.tipOrder = cs.tipOrder.filter((id) => id !== tipId);
          state.activeSession.cheatSheet = cs;
          state.activeSession.lastUpdated = Date.now();
        });
      },

      reorderCheatSheetTip: (tipId, direction) => {
        set((state) => {
          if (!state.activeSession) return;
          const cs = state.activeSession.cheatSheet ?? createDefaultCheatSheet();
          const order = cs.tipOrder;
          const idx = order.indexOf(tipId);
          if (idx < 0) return;
          const targetIdx = direction === 'up' ? idx - 1 : idx + 1;
          if (targetIdx < 0 || targetIdx >= order.length) return;
          [order[idx], order[targetIdx]] = [order[targetIdx], order[idx]];
          state.activeSession.cheatSheet = cs;
          state.activeSession.lastUpdated = Date.now();
        });
      },

      setCombatNotes: (notes) => {
        set((state) => {
          if (!state.activeSession) return;
          const cs = state.activeSession.cheatSheet ?? createDefaultCheatSheet();
          cs.combatNotes = notes;
          state.activeSession.cheatSheet = cs;
          state.activeSession.lastUpdated = Date.now();
        });
      },

      updateCheatSheetTipOrder: (mergedOrder) => {
        set((state) => {
          if (!state.activeSession) return;
          const cs = state.activeSession.cheatSheet ?? createDefaultCheatSheet();
          cs.tipOrder = mergedOrder;
          state.activeSession.cheatSheet = cs;
          state.activeSession.lastUpdated = Date.now();
        });
      },

      toggleBardicInspiration: () => {
        set((state) => {
          if (!state.activeSession) return;
          state.activeSession.bardicInspiration = !state.activeSession.bardicInspiration;
          state.activeSession.lastUpdated = Date.now();
        });
      },

      toggleDmInspiration: () => {
        set((state) => {
          if (!state.activeSession) return;
          state.activeSession.dmInspiration = !state.activeSession.dmInspiration;
          state.activeSession.lastUpdated = Date.now();
        });
      },

      addCustomEffect: (name, description) => {
        set((state) => {
          if (!state.activeSession) return;
          state.activeSession.customEffects.push({
            id: crypto.randomUUID(),
            name,
            description,
          });
          state.activeSession.lastUpdated = Date.now();
        });
      },

      removeCustomEffect: (id) => {
        set((state) => {
          if (!state.activeSession) return;
          state.activeSession.customEffects = state.activeSession.customEffects.filter(
            (e) => e.id !== id,
          );
          state.activeSession.lastUpdated = Date.now();
        });
      },

      shortRest: () => {
        set((state) => {
          if (!state.activeSession) return;
          // Warlock pact slots recover on short rest
          const pactSlot = state.activeSession.spellSlots['pact'];
          if (pactSlot) {
            pactSlot.used = 0;
          }
          // Note: Hit dice spending happens manually via useHitDie
          state.activeSession.lastUpdated = Date.now();
        });
      },

      longRest: () => {
        set((state) => {
          if (!state.activeSession) return;

          const effects = calculateLongRestEffects(
            state.activeSession.hitDice,
            state.activeSession.exhaustionLevel,
          );

          // Restore all HP
          state.activeSession.currentHp = state.activeSession.maxHp;
          state.activeSession.tempHp = 0;

          // Reset death saves
          state.activeSession.deathSaveSuccesses = 0;
          state.activeSession.deathSaveFailures = 0;

          // Restore all spell slots
          Object.values(state.activeSession.spellSlots).forEach((slot) => {
            slot.used = 0;
          });

          // Restore hit dice per computed restorations
          for (const [dieType, amount] of Object.entries(effects.hitDiceRestorations)) {
            state.activeSession.hitDice[dieType].used = Math.max(
              0,
              state.activeSession.hitDice[dieType].used - amount,
            );
          }

          // Restore all class resources
          Object.values(state.activeSession.classResources).forEach((resource) => {
            resource.used = 0;
          });

          // Reduce exhaustion by 1
          if (effects.reduceExhaustion) {
            state.activeSession.exhaustionLevel -= 1;
          }

          state.activeSession.lastUpdated = Date.now();
        });
      },

      clearSession: () => {
        set((state) => {
          state.activeSession = null;
        });
      },

      getExportableRuntimeState: () => {
        const state = get();
        if (!state.activeSession) return null;

        const session = state.activeSession;
        return {
          hitPoints: {
            current: session.currentHp,
            max: session.maxHp,
            temporary: session.tempHp,
          },
          deathSaves: {
            successes: session.deathSaveSuccesses,
            failures: session.deathSaveFailures,
          },
          spellSlots: session.spellSlots,
          hitDice: session.hitDice,
          classResources: session.classResources,
          conditions: session.activeConditions,
          exhaustionLevel: session.exhaustionLevel,
          concentrationSpell: session.concentrationSpell,
          sessionNotes: session.sessionNotes || undefined,
          bardicInspiration: session.bardicInspiration || undefined,
          dmInspiration: session.dmInspiration || undefined,
          customEffects: session.customEffects.length > 0 ? session.customEffects : undefined,
          cheatSheet: (session.cheatSheet &&
            (session.cheatSheet.dismissedTipIds.length > 0 ||
             session.cheatSheet.customTips.length > 0 ||
             session.cheatSheet.tipOrder.length > 0 ||
             session.cheatSheet.combatNotes))
            ? session.cheatSheet
            : undefined,
        };
      },

      restoreRuntimeState: (runtimeState) => {
        set((state) => {
          if (!state.activeSession) return;

          // Restore HP
          state.activeSession.currentHp = runtimeState.hitPoints.current;
          state.activeSession.maxHp = runtimeState.hitPoints.max;
          state.activeSession.tempHp = runtimeState.hitPoints.temporary;

          // Restore death saves
          state.activeSession.deathSaveSuccesses = runtimeState.deathSaves.successes;
          state.activeSession.deathSaveFailures = runtimeState.deathSaves.failures;

          // Restore spell slots (merge with existing to preserve max values if different)
          for (const [level, slot] of Object.entries(runtimeState.spellSlots)) {
            if (state.activeSession.spellSlots[level]) {
              state.activeSession.spellSlots[level].used = slot.used;
              // Use max from import if character's slots changed
              if (slot.max > 0) {
                state.activeSession.spellSlots[level].max = slot.max;
              }
            } else {
              state.activeSession.spellSlots[level] = { ...slot };
            }
          }

          // Restore hit dice
          for (const [dieType, hitDie] of Object.entries(runtimeState.hitDice)) {
            if (state.activeSession.hitDice[dieType]) {
              state.activeSession.hitDice[dieType].used = hitDie.used;
              if (hitDie.max > 0) {
                state.activeSession.hitDice[dieType].max = hitDie.max;
              }
            } else {
              state.activeSession.hitDice[dieType] = { ...hitDie };
            }
          }

          // Restore class resources
          for (const [resourceName, resource] of Object.entries(runtimeState.classResources)) {
            if (state.activeSession.classResources[resourceName]) {
              state.activeSession.classResources[resourceName].used = resource.used;
              if (resource.max > 0) {
                state.activeSession.classResources[resourceName].max = resource.max;
              }
            } else {
              state.activeSession.classResources[resourceName] = { ...resource };
            }
          }

          // Restore conditions
          state.activeSession.activeConditions = runtimeState.conditions as ConditionName[];
          state.activeSession.exhaustionLevel = runtimeState.exhaustionLevel;

          // Restore concentration
          state.activeSession.concentrationSpell = runtimeState.concentrationSpell;

          // Restore session notes if present
          if (runtimeState.sessionNotes !== undefined) {
            state.activeSession.sessionNotes = runtimeState.sessionNotes;
          }

          // Restore inspirations and custom effects if present
          if (runtimeState.bardicInspiration !== undefined) {
            state.activeSession.bardicInspiration = runtimeState.bardicInspiration;
          }
          if (runtimeState.dmInspiration !== undefined) {
            state.activeSession.dmInspiration = runtimeState.dmInspiration;
          }
          if (runtimeState.customEffects !== undefined) {
            state.activeSession.customEffects = runtimeState.customEffects;
          }

          // Restore cheat sheet if present
          if (runtimeState.cheatSheet !== undefined) {
            state.activeSession.cheatSheet = runtimeState.cheatSheet;
          }

          state.activeSession.lastUpdated = Date.now();
        });
      },
    })),
    {
      name: 'play-session-storage',
      storage: createJSONStorage(() => localStorage),
    }
  )
);
